import { useEffect, useState } from "react";
import { api } from "@/lib/api";

const fmtMoney = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(n || 0));

const STATUS_STYLE = {
  draft:      "bg-slate-100 text-slate-700",
  processing: "bg-amber-100 text-amber-800",
  paid:       "bg-emerald-100 text-emerald-800",
  failed:     "bg-rose-100 text-rose-800",
  cancelled:  "bg-slate-200 text-slate-700",
};

function PayoutStatus({ status }) {
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${STATUS_STYLE[status] || "bg-slate-100 text-slate-700"}`}>
      {status}
    </span>
  );
}

function StatCard({ label, value, testid, sub }) {
  return (
    <div className="border border-slate-200 rounded-md bg-white p-5" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">{label}</div>
      <div className="font-display text-2xl sm:text-3xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

export default function AffiliatePayouts() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/me/affiliate/payouts").then((r) => setData(r.data)).catch(() => setData({ payouts: [], payable_total: 0, pending_total: 0, paid_lifetime: 0 })); }, []);
  if (!data) return <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading payouts…</div>;

  return (
    <div className="space-y-6" data-testid="affiliate-payouts-page">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard label="Available for Payout" value={fmtMoney(data.payable_total)} testid="payable-total" sub="Cleared the hold period" />
        <StatCard label="Pending" value={fmtMoney(data.pending_total)} testid="pending-total" sub="Still within hold period" />
        <StatCard label="Lifetime Paid" value={fmtMoney(data.paid_lifetime)} testid="paid-lifetime" />
      </div>

      <div className="border border-slate-200 rounded-md bg-blue-50 p-4 text-sm text-slate-700" data-testid="payout-info-note">
        Affiliate payouts are processed manually. Contact support if your payout information needs to be updated.
      </div>

      <section aria-labelledby="payout-history">
        <div id="payout-history" className="eyebrow mb-2">Payout History</div>
        <div className="border border-slate-200 rounded-md bg-white overflow-hidden" data-testid="payouts-table">
          {data.payouts.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">No payouts yet. Earn payable commission first — it clears after the hold period.</div>
          )}
          {data.payouts.length > 0 && (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Amount</th>
                  <th className="text-left px-3 py-2">Method</th>
                  <th className="text-left px-3 py-2 hidden sm:table-cell">Reference</th>
                  <th className="text-left px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.payouts.map((p) => (
                  <tr key={p.payout_id} className="border-t border-slate-200" data-testid={`payout-row-${p.payout_id}`}>
                    <td className="px-3 py-2 text-xs text-slate-600">{(p.paid_at || p.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 text-right font-semibold">{fmtMoney(p.amount)}</td>
                    <td className="px-3 py-2 text-xs">{p.payment_method || "manual"}</td>
                    <td className="px-3 py-2 text-xs font-mono text-slate-500 hidden sm:table-cell">{p.reference_id || "—"}</td>
                    <td className="px-3 py-2"><PayoutStatus status={p.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
